import { Injectable } from '@angular/core';

import { FormsGlobals } from '../../../../models/forms/forms.enum';
import { FormVersionConfigurationModel } from '../../../../models/forms/form-version-configuration.model';
import { FormParagraphConfigurationModel } from '../../../../models/forms/form-paragraph-configuration.model';
import { FormItemConfigurationModel } from '../../../../models/forms/form-item-configuration.model';
import { EnumValidValueItemModel } from '../../../../models/enum/enum-valid-value-item.model';
import { FormItemCategory, FormsConstants } from '../../models/forms-constants';
import {
  AiExistingParagraphContext,
  AiFormCatalogSnapshot,
  AiGeneratedFormDraft,
  AiGeneratedFormItem,
  AiTemplateField
} from '../models/ai-form-draft.model';

export interface PrebuiltTemplateField extends AiTemplateField {
  icon: string;
}

export interface PrebuiltTemplateGroup {
  category: string;
  icon: string;
  fields: PrebuiltTemplateField[];
}

export interface CustomTemplateGroup extends PrebuiltTemplateGroup {
  id: string;
  createdOn: string;
}

/**
 * Everything an AI-draft application run needs from its host. The paragraph builder passes its
 * live component state; the form finder passes freshly-fetched equivalents. When `isPreview` is
 * true, per-item usage telemetry is suppressed. Warning callbacks (`onLinkWarnings`,
 * `onNamingWarnings`) fire regardless of `isPreview` — both current hosts apply drafts with
 * `isPreview: true` even for an accepted turn, so gating warnings on it would make them
 * unreachable.
 */
export interface AiDraftApplyContext {
  validValues: any[];
  templateFields: AiTemplateField[];
  getGuids: (count: number) => Promise<string[]>;
  isPreview: boolean;
  onControlUsed?: (controlTypeId: string) => void;
  onLinkWarnings?: (warnings: string[]) => void;
  onNamingWarnings?: (warnings: string[]) => void;
}

/**
 * Converts AI-generated form drafts into form version configuration, shared by the paragraph
 * builder's AI Form Studio and the form finder's PDF-import-as-new-version flow. All methods are
 * host-agnostic: state they need arrives via parameters or an AiDraftApplyContext.
 */
@Injectable({ providedIn: 'root' })
export class FormsBuilderAiDraftService {
  // Prebuilt field templates — single source of truth (also rendered in the paragraph builder's
  // quick-add panel).
  readonly prebuiltTemplateGroups: PrebuiltTemplateGroup[] = [
    {
      category: 'Contact Info',
      icon: 'person',
      fields: [
        { name: 'Full Name', label: 'Full Name', controlType: '00000001-1000-0000-0000-000000000001', icon: 'person', composite: true },
        { name: 'First Name', label: 'First Name', controlType: '00000001-1000-0000-0000-000000000001', icon: 'person', composite: true },
        { name: 'Last Name', label: 'Last Name', controlType: '00000001-1000-0000-0000-000000000001', icon: 'person', composite: true },
        { name: 'Birthdate', label: 'Birthdate', controlType: '00000001-1000-0000-0000-000000000011', icon: 'cake', composite: true},
        { name: 'Email', label: 'Email Address', controlType: '00000001-1000-0000-0000-000000000001', icon: 'email', composite: true },
        { name: 'Phone', label: 'Phone Number', controlType: '00000001-1000-0000-0000-000000000028', icon: 'phone', composite: true },
        { name: 'Address', label: 'Address', controlType: '00000001-1000-0000-0000-000000000001', icon: 'home', composite: true },
        { name: 'City', label: 'City', controlType: '00000001-1000-0000-0000-000000000001', icon: 'location_city', composite: true },
        { name: 'State', label: 'State', controlType: '00000001-1000-0000-0000-000000000001', icon: 'map', composite: true },
        { name: 'Zip Code', label: 'Zip Code', controlType: '00000001-1000-0000-0000-000000000001', icon: 'markunread_mailbox', composite: true },
        { name: 'Social Security', label: 'Soc. Sec#', controlType: '00000001-1000-0000-0000-000000000001', icon: 'badge', composite: true },
      ]
    },
    {
      category: 'Common Fields',
      icon: 'assignment',
      fields: [
        { name: 'Date', label: 'Date', controlType: '00000001-1000-0000-0000-000000000011', icon: 'event' },
        { name: 'Signature', label: 'Signature', controlType: '00000001-1000-0000-0000-000000000027', icon: 'draw' },
        { name: 'Initial', label: 'Initial', controlType: '00000001-1000-0000-0000-000000000001', icon: 'edit', composite: true, labelWidth: 5, inputWidth: 5 },
        { name: 'Comments', label: 'Comments', controlType: '00000001-1000-0000-0000-000000000025', icon: 'comment' },
        { name: 'Heading', label: 'Heading', controlType: '00000001-1000-0000-0000-000000000002', icon: 'format_size', defaultValue: '24'},
        { name: 'Subheading', label: 'Subheading', controlType: '00000001-1000-0000-0000-000000000018', icon: 'format_size' },
        { name: 'Heading 3', label: 'Heading 3', controlType: '00000001-1000-0000-0000-000000000002', icon: 'format_size', defaultValue: '16' },

      ]
    },
    {
      category: 'Radio Options',
      icon: 'radio_button_checked',
      fields: [
        { name: 'Yes/No', label: 'Yes/No', controlType: '00000001-1000-0000-0000-000000000004', icon: 'radio_button_checked', validValueName: 'enumFRMYesNo' },
        { name: 'Yes/No/Possibly', label: 'Yes/No/Possibly', controlType: '00000001-1000-0000-0000-000000000004', icon: 'radio_button_checked', validValueName: 'enumYesNoPos' },
        { name: 'Sí/No', label: 'Sí/No', controlType: '00000001-1000-0000-0000-000000000004', icon: 'radio_button_checked', validValueName: 'enumFRMSiNo' },
        { name: 'Sí/No/Posiblemente', label: 'Sí/No/Posiblemente', controlType: '00000001-1000-0000-0000-000000000004', icon: 'radio_button_checked', validValueName: 'enumSiNoPos' },
      ]
    },
    {
      category: 'Checkbox Options',
      icon: 'check_box',
      fields: [
        { name: 'Former Illnesses', label: 'Former Illnesses', controlType: '00000001-1000-0000-0000-000000000005', icon: 'apps', validValueName: 'enumFormIllness' },
        { name: 'Pain Characterization', label: 'Pain Characterization', controlType: '00000001-1000-0000-0000-000000000005', icon: 'apps', validValueName: 'enumEndoPainCharacterOfPain ' },
      ]
    },
    {
      category: 'Medical',
      icon: 'medical_services',
      fields: [
        { name: 'Date of Birth', label: 'Date of Birth', controlType: '00000001-1000-0000-0000-000000000011', icon: 'cake' },
        { name: 'Allergies', label: 'Known Allergies', controlType: '00000001-1000-0000-0000-000000000025', icon: 'warning' },
        { name: 'Medications', label: 'Current Medications', controlType: '00000001-1000-0000-0000-000000000025', icon: 'medication' },
        { name: 'Emergency Contact', label: 'Emergency Contact', controlType: '00000001-1000-0000-0000-000000000001', icon: 'emergency', composite: true },
        { name: 'Emergency Phone', label: 'Emergency Phone', controlType: '00000001-1000-0000-0000-000000000028', icon: 'phone_in_talk', composite: true },
      ]
    }
  ];

  constructor(private globals: FormsGlobals) {}

  /** User-saved template groups persisted by the paragraph builder's quick-add panel. */
  loadCustomTemplateGroups(): CustomTemplateGroup[] {
    const stored = localStorage.getItem('paragraphBuilder.customTemplates');
    if (!stored) {
      return [];
    }
    try {
      return JSON.parse(stored) as CustomTemplateGroup[];
    } catch (e) {
      console.error('Failed to load custom templates:', e);
      return [];
    }
  }

  /** Flattened field list across template groups, for AiDraftApplyContext.templateFields. */
  flattenTemplateFields(groups: Array<{ fields: AiTemplateField[] }>): AiTemplateField[] {
    return (groups || []).flatMap(group => group.fields || []);
  }

  /**
   * Applies an AI draft to a throwaway clone of `base` using server-issued GUIDs, returning the
   * updated clone plus the GUIDs of paragraphs that were added or replaced (for the Studio's
   * change highlight). The input `base` is never mutated — the Studio keeps it as the undo point.
   * PDF mode soft-deletes all existing paragraphs first to mirror the replace-all import behavior.
   */
  async applyDraftToWorkingVersion(
    base: FormVersionConfigurationModel,
    draft: AiGeneratedFormDraft,
    mode: 'pdf' | 'edit',
    ctx: AiDraftApplyContext
  ): Promise<{
    version: FormVersionConfigurationModel;
    changedIds: string[];
    applyWarnings: string[];
  }> {
    const clone: FormVersionConfigurationModel = JSON.parse(JSON.stringify(base));
    if (mode === 'pdf') {
      (clone.cfgDFormParagraphs || []).forEach(p => { p.xauditDeleted = true; });
    }

    // Link and naming warnings are raised deep inside the apply via context callbacks, which were
    // fire-and-forget: the only durable record was a console.warn. Collect them here so the caller
    // gets them back with the version and can show them somewhere the user can actually read them.
    // The host's own callbacks still fire, so this stays additive for anything already wired.
    const applyWarnings: string[] = [];
    const collectingCtx: AiDraftApplyContext = {
      ...ctx,
      onLinkWarnings: warnings => {
        applyWarnings.push(...warnings);
        ctx.onLinkWarnings?.(warnings);
      },
      onNamingWarnings: warnings => {
        applyWarnings.push(...warnings);
        ctx.onNamingWarnings?.(warnings);
      }
    };

    const changed = await this.applyDraftToVersion(clone, draft, mode, collectingCtx);
    return { version: clone, changedIds: changed.map(p => p.DFormParagraphId), applyWarnings };
  }

  buildExistingParagraphSnapshot(version: FormVersionConfigurationModel): AiExistingParagraphContext[] {
    return (version?.cfgDFormParagraphs || [])
      .filter(p => !p.xauditDeleted)
      .sort((a, b) => (a.ParagraphOrder || 0) - (b.ParagraphOrder || 0))
      .map((p, i) => ({
        index: i,
        paragraphName: p.ParagraphName || '',
        itemLabels: (p.cfgDFormItems || [])
          .filter(item => !item.xauditDeleted && item.Label)
          .map(item => item.Label)
      }));
  }

  buildCatalogSnapshot(
    templateGroupSources: Array<{ category: string; fields: AiTemplateField[] }>,
    validValues: any[]
  ): AiFormCatalogSnapshot {
    const controls = FormsConstants.cfgFormItemTypeList.map(item => ({
      id: item.id,
      name: item.name,
      category: item.category,
      description: item.description,
      aliases: item.aliases || [],
      aiHints: item.aiHints || []
    }));

    const templateGroups = (templateGroupSources || []).map(group => ({
      category: group.category,
      fields: (group.fields || []).map((field: AiTemplateField) => ({ ...field }))
    }));

    const validValueNameSet = new Set<string>();
    (validValues || [])
      .map((value: any) => (value?.ValidValueName || '').trim())
      .filter((name: string) => !!name)
      .forEach((name: string) => validValueNameSet.add(name));

    templateGroups
      .flatMap(group => group.fields || [])
      .map(field => (field?.validValueName || '').trim())
      .filter((name: string) => !!name)
      .forEach((name: string) => validValueNameSet.add(name));

    const validValueNames = Array.from(validValueNameSet);

    return {
      controls,
      validValueNames,
      templateGroups
    };
  }

  /**
   * Shared draft → configuration conversion. Applies the AI draft's replacements, deletions, and
   * insertions onto the supplied `version` (which may be the live form or a throwaway clone),
   * sourcing GUIDs from `ctx.getGuids`. When `ctx.isPreview` is true, per-item usage telemetry is
   * suppressed. Returns the paragraphs that were added or replaced.
   */
  async applyDraftToVersion(
    version: FormVersionConfigurationModel,
    draft: AiGeneratedFormDraft,
    mode: 'pdf' | 'edit',
    ctx: AiDraftApplyContext
  ): Promise<FormParagraphConfigurationModel[]> {
    // Snapshot of active paragraphs in display order before any mutations.
    const activeSorted = (version.cfgDFormParagraphs || [])
      .filter(p => !p.xauditDeleted)
      .sort((a, b) => (a.ParagraphOrder || 0) - (b.ParagraphOrder || 0));

    const addedParagraphs: FormParagraphConfigurationModel[] = [];

    // 1. REPLACEMENTS — overwrite content of existing paragraphs in place.
    const replacedIndexes = new Set<number>();
    for (const paragraphDraft of (draft.paragraphs || [])) {
      if (paragraphDraft.replaceAtIndex == null) continue;
      const existing = activeSorted[paragraphDraft.replaceAtIndex];
      if (!existing) continue;

      replacedIndexes.add(paragraphDraft.replaceAtIndex);

      // Tombstone the outgoing items (the save layer turns these into backend deletions) and park
      // them outside the live grid, so a replaced item can never share a row/column with its
      // replacement even if a renderer forgets to filter xauditDeleted.
      (existing.cfgDFormItems || []).forEach(item => {
        item.xauditDeleted = true;
        item.ParagraphColumn = 99999;
        item.ItemOrder = 99999;
      });
      existing.cfgDFormItems = existing.cfgDFormItems || [];

      const clampedExistingName = this.clampToLength(
        paragraphDraft.paragraphName, FormsBuilderAiDraftService.MAX_PARAGRAPH_NAME_LENGTH);
      existing.ParagraphName = clampedExistingName || existing.ParagraphName;
      existing.enumParagraphOrientationID = this.resolveOrientationId(paragraphDraft.orientation);
      existing.enumItemLayoutID = this.resolveLayoutId(paragraphDraft.layout);
      existing.isRepeting = paragraphDraft.isRepeating === true;

      const rows = paragraphDraft.rows || [];
      for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
        const row = rows[rowIndex] || [];
        const expandedRowItems: FormItemConfigurationModel[] = [];
        for (const draftItem of row) {
          const rowItems = await this.buildItemsFromAiDraft(existing.DFormParagraphId, draftItem, ctx);
          expandedRowItems.push(...rowItems);
        }
        expandedRowItems.forEach((item, columnIndex) => {
          item.ParagraphRow = rowIndex;
          item.ParagraphColumn = columnIndex;
          item.ItemOrder = columnIndex;
          existing.cfgDFormItems.push(item);
        });
      }

      addedParagraphs.push(existing);
    }

    // 2. DELETIONS — soft-delete existing paragraphs (skip already-replaced ones).
    for (const deleteIndex of (draft.deleteParagraphIndexes || [])) {
      if (replacedIndexes.has(deleteIndex)) continue;
      const existing = activeSorted[deleteIndex];
      if (existing) {
        existing.xauditDeleted = true;
      }
    }

    // 3. INSERTIONS — create and splice in brand-new paragraphs.
    for (const paragraphDraft of (draft.paragraphs || [])) {
      if (paragraphDraft.replaceAtIndex != null) continue;

      const paragraph = new FormParagraphConfigurationModel();
      paragraph.DFormVersionId = version.DFormVersionId;
      paragraph.xauditDeleted = false;
      paragraph.cfgDFormItems = [];
      paragraph.ParagraphName = this.clampToLength(
        paragraphDraft.paragraphName, FormsBuilderAiDraftService.MAX_PARAGRAPH_NAME_LENGTH)
        || `Generated Paragraph`;
      paragraph.ParagraphOrder = 0;
      paragraph.enumParagraphOrientationID = this.resolveOrientationId(paragraphDraft.orientation);
      paragraph.enumItemLayoutID = this.resolveLayoutId(paragraphDraft.layout);
      paragraph.PageBreakAfter = false;
      paragraph.isRepeting = paragraphDraft.isRepeating === true;
      paragraph.isFirstRowHeader = false;

      const [paragraphId] = await ctx.getGuids(1);
      paragraph.DFormParagraphId = paragraphId;

      const rows = paragraphDraft.rows || [];
      for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
        const row = rows[rowIndex] || [];
        const expandedRowItems: FormItemConfigurationModel[] = [];
        for (const draftItem of row) {
          const rowItems = await this.buildItemsFromAiDraft(paragraph.DFormParagraphId, draftItem, ctx);
          expandedRowItems.push(...rowItems);
        }
        expandedRowItems.forEach((item, columnIndex) => {
          item.ParagraphRow = rowIndex;
          item.ParagraphColumn = columnIndex;
          item.ItemOrder = columnIndex;
          paragraph.cfgDFormItems.push(item);
        });
      }

      if (mode === 'pdf' || paragraphDraft.insertAfterIndex === undefined || paragraphDraft.insertAfterIndex === null || paragraphDraft.insertAfterIndex === -1) {
        version.cfgDFormParagraphs.push(paragraph);
      } else {
        const insertAfter = activeSorted[paragraphDraft.insertAfterIndex];
        const splicePos = insertAfter
          ? version.cfgDFormParagraphs.indexOf(insertAfter) + 1
          : version.cfgDFormParagraphs.length;
        version.cfgDFormParagraphs.splice(splicePos, 0, paragraph);
      }

      addedParagraphs.push(paragraph);
    }

    // Note Builder: clinical history forms (medical/pain/medication) carry note-enabled items.
    // Establish the question→answer hierarchy so fill-time note assembly can group each question
    // with its radio answer and "Please explain" text. Non-clinical forms have no note-enabled
    // items and are left untouched. Runs BEFORE the medical-history naming pass below: that pass's
    // antibiotics handling parents the "Please indicate" follow-up to the specific antibiotics item
    // (nameAntibioticsFollowUp), which must win over this generic preceding-question link — reversing
    // the order would let this pass's blanket reparenting clobber that more specific link.
    const hierarchyWarnings: string[] = [];
    if (this.draftHasNoteEnabledItems(draft)) {
      for (const paragraph of addedParagraphs) {
        this.linkClinicalQuestionHierarchy(paragraph, hierarchyWarnings);
      }
    }

    // Critical sensitivity / medication history Field ID canonicalization applies to every
    // AI-added or -replaced paragraph, regardless of whether that particular draft happens to
    // enable Note Builder — a plain "add a pregnancy question" edit carries no note-enabled items
    // but must still get the exact "Pregnant" Field ID the read side requires.
    //
    // claimedTerms is shared across every paragraph in this call (not reset per paragraph) and
    // seeded from sensitivities already claimed by untouched paragraphs elsewhere in the form, so
    // an edit to one paragraph can't silently mint a second "Pregnant" (etc.) Field ID that already
    // exists on an active paragraph this draft didn't touch.
    const namingWarnings: string[] = [];
    const addedParagraphSet = new Set(addedParagraphs);
    const claimedTerms = new Set<string>();
    for (const paragraph of (version.cfgDFormParagraphs || [])) {
      if (paragraph.xauditDeleted || addedParagraphSet.has(paragraph)) continue;
      for (const item of (paragraph.cfgDFormItems || [])) {
        if (item.xauditDeleted) continue;
        const existingSensitivity = this.CRITICAL_SENSITIVITY_TERMS.find(s => s.itemName === item.DFormItemName);
        if (existingSensitivity) claimedTerms.add(existingSensitivity.term);
      }
    }
    for (const paragraph of addedParagraphs) {
      this.applyMedicalHistoryItemNames(paragraph, namingWarnings, claimedTerms);
      this.applyMedicationHistoryItemNames(paragraph);
    }

    // Both current hosts apply drafts with ctx.isPreview: true even for an accepted turn (see
    // AiDraftApplyContext), so gating these on isPreview would make them permanently unreachable.
    if (hierarchyWarnings.length) {
      console.warn('Note Builder hierarchy warnings', hierarchyWarnings);
      ctx.onLinkWarnings?.(hierarchyWarnings);
    }
    if (namingWarnings.length) {
      console.warn('AI draft item-naming warnings', namingWarnings);
      ctx.onNamingWarnings?.(namingWarnings);
    }

    // Repeating paragraphs (medication history, emergency contacts, etc.) rely on their Action
    // Button's "Repeat Paragraph" after-update action to add another row. This applies to any
    // repeating paragraph regardless of Note Builder/clinical form type, so it runs unconditionally.
    for (const paragraph of addedParagraphs) {
      this.enforceRepeatParagraphAction(paragraph);
    }

    this.recalculateParagraphOrder(version);

    return addedParagraphs;
  }

  recalculateParagraphOrder(version: FormVersionConfigurationModel): void {
    (version.cfgDFormParagraphs || [])
      .filter(p => !p.xauditDeleted)
      .sort((a, b) => (a.ParagraphOrder || 0) - (b.ParagraphOrder || 0))
      .forEach((p, i) => { p.ParagraphOrder = i + 1; });
  }

  // cfgDFormParagraph.ParagraphName is nvarchar(50); cfgDFormItem.DefaultValue is nvarchar(500).
  // Entity Framework validates these lengths before it builds the SQL, so an over-long value does
  // not surface as a save error against a field — it throws DbEntityValidationException out of
  // saveDuplicateCfgVersion and the import dies with a stack trace naming no column. The server
  // clamps AI output too; this keeps the guarantee on anything assembled or edited here after that.
  private static readonly MAX_PARAGRAPH_NAME_LENGTH = 50;
  private static readonly MAX_DEFAULT_VALUE_LENGTH = 500;

  private clampToLength(value: string | null | undefined, maxLength: number): string {
    const text = (value ?? '').trim();
    return text.length > maxLength ? text.substring(0, maxLength).trimEnd() : text;
  }

  private resolveOrientationId(orientation: string | undefined): string {
    const normalized = (orientation || '').toLowerCase();
    if (normalized.includes('vertical')) {
      return this.globals.PARAGRAPH_LAYOUT_VERTICAL;
    }

    if (normalized.includes('horizontal')) {
      return this.globals.PARAGRAPH_LAYOUT_HORIZONTAL;
    }

    return this.globals.PARAGRAPH_LAYOUT_HORIZONTAL;
  }

  private resolveLayoutId(layout: string | undefined): string {
    const normalized = (layout || '').toLowerCase();
    if (normalized.includes('two')) {
      return this.globals.ITEM_LAYOUT_TWO_LINE;
    }

    if (normalized.includes('side') || normalized.includes('sbs')) {
      return this.globals.ITEM_LAYOUT_SBS;
    }

    return this.globals.ITEM_LAYOUT_SBS;
  }

  private async buildItemsFromAiDraft(
    paragraphId: string,
    draftItem: AiGeneratedFormItem,
    ctx: AiDraftApplyContext
  ): Promise<FormItemConfigurationModel[]> {
    const template = this.findTemplateForAiItem(draftItem, ctx.templateFields);
    const controlTypeId = this.resolveControlTypeId(draftItem.controlTypeId);
    const isParagraphControl = controlTypeId === this.globals.CONTROL_LABEL;
    const useComposite = !isParagraphControl && !!draftItem.compositeHint;

    if (useComposite) {
      const guids = await ctx.getGuids(2);
      const labelItem = new FormItemConfigurationModel();
      labelItem.DFormItemId = guids[0];
      labelItem.DFormParagraphId = paragraphId;
      labelItem.enumFormControlID = this.globals.CONTROL_LABEL;
      labelItem.DFormItemName = `${draftItem.name || 'Field'} Label`;
      labelItem.Label = `${draftItem.label || draftItem.name || 'Field'}:`;
      labelItem.ParagraphColumnWidth = template?.labelWidth ?? 30;
      labelItem.xauditDeleted = false;
      labelItem.isRequired = false;

      const inputItem = new FormItemConfigurationModel();
      inputItem.DFormItemId = guids[1];
      inputItem.DFormParagraphId = paragraphId;
      inputItem.enumFormControlID = controlTypeId;
      inputItem.DFormItemName = draftItem.name || this.getControlNameById(controlTypeId);
      inputItem.Label = '';
      inputItem.DefaultValue = this.clampToLength(
        draftItem.defaultValue || template?.defaultValue,
        FormsBuilderAiDraftService.MAX_DEFAULT_VALUE_LENGTH) || null;
      inputItem.ParagraphColumnWidth = draftItem.columnWidth ?? template?.inputWidth ?? 70;
      inputItem.isRequired = !!draftItem.required && !this.isDisplayOnlyControl(controlTypeId);
      inputItem.xauditDeleted = false;
      this.applyValidValueToItem(inputItem, draftItem.validValueName || template?.validValueName, ctx.validValues);
      this.applyNoteBuilderToItem(inputItem, draftItem);
      if (!ctx.isPreview) ctx.onControlUsed?.(controlTypeId);

      return [labelItem, inputItem];
    }

    const [itemId] = await ctx.getGuids(1);
    const item = new FormItemConfigurationModel();
    item.DFormItemId = itemId;
    item.DFormParagraphId = paragraphId;
    item.enumFormControlID = controlTypeId;
    item.DFormItemName = draftItem.name || this.getControlNameById(controlTypeId);
    item.Label = draftItem.label || draftItem.name || '';
    item.DefaultValue = this.clampToLength(
      draftItem.defaultValue || template?.defaultValue,
      FormsBuilderAiDraftService.MAX_DEFAULT_VALUE_LENGTH) || null;
    item.ParagraphColumnWidth = draftItem.columnWidth || 0;
    item.isRequired = !!draftItem.required && !this.isDisplayOnlyControl(controlTypeId);
    item.xauditDeleted = false;
    item.enumLabelAligmentID = draftItem.enumLabelAligmentID || '';
    item.AfterUpdate_ItemActionId = draftItem.afterUpdateActionId || null;
    this.applyValidValueToItem(item, draftItem.validValueName || template?.validValueName, ctx.validValues);
    this.applyNoteBuilderToItem(item, draftItem);
    if (!ctx.isPreview) ctx.onControlUsed?.(controlTypeId);

    return [item];
  }

  /**
   * Applies AI-generated Note Builder configuration to a form item. When the draft item has
   * note generation enabled, copies the note text and include-value flag, and — for items backed
   * by a valid value (e.g. a Yes/No radio list) — resolves the trigger value string (e.g. "Yes")
   * to its ValidValueItemId GUID so the note only emits when that answer is selected (see
   * findTriggerItem for the matching rules). Items with no valid value (single checkboxes, short
   * answers) or an unmatched trigger leave the trigger unset and emit on checked/typed value,
   * matching the form-submission note logic.
   */
  private applyNoteBuilderToItem(item: FormItemConfigurationModel, draftItem: AiGeneratedFormItem): void {
    if (!draftItem.isNoteEnabled) {
      return;
    }

    item.isNoteEnabled = true;
    item.Note = draftItem.note || '';
    item.IncludeValueInNote = !!draftItem.includeValueInNote;

    const trigger = this.findTriggerItem(item.ValidValue?.enumValidValueItems || [], draftItem.noteTriggerValue);
    if (trigger) {
      item.NoteTriggerValidValueItemId = trigger.ValidValueItemId;
    }
  }

  /**
   * Finds the valid-value option an AI noteTriggerValue refers to. Pass 1 matches ItemName either in
   * full ("enumYesNoPos.Yes") or with any "<ListName>." prefix dropped, so "Yes" resolves on
   * enumYesNoPos as well as on the plain-named lists (enumFRMYesNo, enumFRMSiNo, enumSiNoPos). Pass 2,
   * only when pass 1 finds nothing, matches ItemDisplayValue, so "Sí" resolves on the Spanish lists.
   * Both passes are exact, case-insensitive and accent-insensitive — never a prefix/substring match,
   * so "Yes"/"Sí" can never land on a No, Possibly or Posiblemente option.
   */
  private findTriggerItem(items: EnumValidValueItemModel[], triggerValue: string | undefined): EnumValidValueItemModel | null {
    const trigger = this.normalizeTriggerToken(triggerValue);
    if (!trigger || !items.length) {
      return null;
    }

    const byName = items.find(vi => {
      const name = vi.ItemName || '';
      return this.normalizeTriggerToken(name) === trigger
        || this.normalizeTriggerToken(name.substring(name.lastIndexOf('.') + 1)) === trigger;
    });
    return byName || items.find(vi => this.normalizeTriggerToken(vi.ItemDisplayValue) === trigger) || null;
  }

  /** Trims, strips diacritics ("Sí" -> "Si") and lower-cases, for trigger comparisons. */
  private normalizeTriggerToken(value: string | undefined): string {
    return (value || '').trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }

  private resolveControlTypeId(controlTypeId: string): string {
    if (FormsConstants.cfgFormItemTypeList.some(item => item.id === controlTypeId)) {
      return controlTypeId;
    }

    return this.globals.CONTROL_TEXT_BOX;
  }

  /**
   * True for text/display controls (Heading, Paragraph, Markup Text, etc.) and Row header. These
   * never receive a result, so a required flag on one leaves the form permanently incomplete
   * (TX-7311). Mirrors the backend's StaticTextControlNames.
   */
  private isDisplayOnlyControl(controlTypeId: string): boolean {
    return FormsConstants.cfgFormItemTypeList.some(c =>
      c.id === controlTypeId && (c.category === FormItemCategory.TextDisplay || c.name === 'Row header'));
  }

  /**
   * True when an AI draft contains any note-enabled item, which only clinical history form
   * types (medical/pain/medication) ever produce. Used to gate the question→answer hierarchy
   * linking so regular forms keep their flat structure.
   */
  private draftHasNoteEnabledItems(draft: AiGeneratedFormDraft): boolean {
    return (draft?.paragraphs || []).some(paragraph =>
      (paragraph?.rows || []).some(row =>
        (row || []).some(item => item?.isNoteEnabled === true)));
  }

  /**
   * True when a persisted form version has any non-deleted Note Builder-enabled item. Used after
   * an AI import is saved (where only the saved version, not the original draft, is available) to
   * decide whether to surface the Note Builder review callout to the user.
   */
  versionHasNoteEnabledItems(version: FormVersionConfigurationModel): boolean {
    return (version?.cfgDFormParagraphs || []).some(paragraph =>
      !paragraph.xauditDeleted && (paragraph.cfgDFormItems || []).some(
        (item: any) => !item.xauditDeleted && item?.isNoteEnabled === true));
  }

  /**
   * Recognizes a "Please explain"/"Please indicate" style follow-up label. Such a Paragraph
   * does NOT start a new question — its text answer still belongs to the preceding clinical
   * question. Mirrors the explanation handling in medical-history-dialog.component.ts.
   */
  private isExplanationLabel(label: string): boolean {
    const text = (label || '').trim().toLowerCase().replace(/:$/, '');
    return text.startsWith('please explain') || text.startsWith('please indicate') || text === 'explain';
  }

  /**
   * Recognizes a "do you require premedication?" style question, however it is phrased.
   * Matches the literal "required to take antibiotic(s)" wording as well as any premedication
   * phrasing ("premedicate", "premedication", "pre-medication", "pre medication") so the
   * canonical "requiredToTakeAntibiotics*" item names are applied to premedication questions
   * in general, not only the antibiotics-specific wording.
   */
  private isPremedicationLabel(label: string): boolean {
    const text = (label || '').toLowerCase();
    return text.includes('required to take antibiotic') ||
      text.includes('premedicat') ||
      text.includes('pre-medicat') ||
      text.includes('pre medicat');
  }

  /**
   * Establishes the question→answer hierarchy within a single paragraph for note-builder forms.
   * Walks the paragraph's non-deleted items in layout order (row, then column) and treats each
   * Paragraph/Label control as the current question (skipping "Please explain" follow-up labels).
   * Radio lists and text boxes are parented to the current question and the question is flagged
   * with HasChildren. Answers with no preceding question are left flat and reported as warnings.
   */
  private linkClinicalQuestionHierarchy(
    paragraph: FormParagraphConfigurationModel,
    warnings: string[]
  ): void {
    const items = (paragraph?.cfgDFormItems || [])
      .filter(item => item && !item.xauditDeleted)
      .sort((a, b) =>
        (a.ParagraphRow || 0) - (b.ParagraphRow || 0) ||
        (a.ParagraphColumn || 0) - (b.ParagraphColumn || 0));

    const labelControlIds = [this.globals.CONTROL_LABEL, this.globals.CONTROL_LABEL_45];
    const answerControlIds = [this.globals.CONTROL_RADIO_LIST, this.globals.CONTROL_TEXT_BOX];

    let currentQuestion: FormItemConfigurationModel | null = null;

    for (const item of items) {
      if (labelControlIds.includes(item.enumFormControlID)) {
        // A follow-up "Please explain" label must not hijack the parent question.
        if (!this.isExplanationLabel(item.Label)) {
          currentQuestion = item;
        }
        continue;
      }

      if (answerControlIds.includes(item.enumFormControlID)) {
        if (currentQuestion) {
          item.ParentDFormItemId = currentQuestion.DFormItemId;
          currentQuestion.HasChildren = true;
        } else {
          warnings.push(
            `Note Builder: answer "${item.Label || item.DFormItemName}" in "${paragraph.ParagraphName}" has no preceding question and was left unlinked.`
          );
        }
      }
    }
  }

  /**
   * Canonical DFormItemName values medical history keys off downstream. Read by
   * medical-history-dialog.component.ts (Critical Sensitivities / Premedication Required boxes)
   * and case-view.component.ts (computeHasCriticalMedicalAlerts). Mirrors
   * MEDICATION_HISTORY_ITEM_NAMES below.
   */
  private readonly MEDICAL_HISTORY_ITEM_NAMES = {
    allergicToLatex: 'AllergicToLatex',
    allergicToEpinephrine: 'AllergicToEpinephrine',
    allergicToAntibiotics: 'AllergicToAntibiotics',
    antibioticsPatientIndicate: 'AntibioticsPatientIndicate',
    premedicationLabel: 'requiredToTakeAntibioticsLbl',
    premedicationAnswer: 'requiredToTakeAntibioticsAns',
    premedicationResponse: 'requiredToTakeAntibioticsRes',
    pregnant: 'Pregnant'
  } as const;

  /**
   * The sensitivities that surface in the Critical Sensitivities box of the Medical History
   * dialog and raise the Case View alert banner. Each is recognized either as a checklist
   * checkbox or as a Yes/No radio. Most are allergies, but pregnancy is a condition, not an
   * allergy — "term" here just means "the word this question is about". Local anesthetic is
   * deliberately absent — it has no canonical name and no slot in either read site, so naming
   * it would have no effect.
   *
   * Pregnancy matches on "pregnant" rather than the "pregnan" stem so that current-status
   * wording ("Are you currently pregnant?") is recognized while pregnancy-test, -history,
   * -complication, and -planning questions (which say "pregnancy", not "pregnant") are not
   * misread as an affirmative current pregnancy.
   */
  private readonly CRITICAL_SENSITIVITY_TERMS: ReadonlyArray<{ term: string; itemName: string }> = [
    { term: 'antibiotic', itemName: this.MEDICAL_HISTORY_ITEM_NAMES.allergicToAntibiotics },
    { term: 'latex', itemName: this.MEDICAL_HISTORY_ITEM_NAMES.allergicToLatex },
    { term: 'epinephrine', itemName: this.MEDICAL_HISTORY_ITEM_NAMES.allergicToEpinephrine },
    { term: 'pregnant', itemName: this.MEDICAL_HISTORY_ITEM_NAMES.pregnant }
  ];

  /**
   * Matches a sensitivity term against both the visible label and the technical item name. A
   * compositeHint row carries its text on a separate Paragraph label and leaves the input's own
   * Label empty (see buildItemsFromAiDraft), so the item name is the only signal available there.
   */
  private matchesSensitivityTerm(item: FormItemConfigurationModel, term: string): boolean {
    return this.textNamesSensitivity(`${item.Label || ''} ${item.DFormItemName || ''}`, term);
  }

  /**
   * True when free text names a critical sensitivity term. Premedication ("required to take
   * antibiotics prior to treatment") and usage ("are you currently taking antibiotics") phrasings
   * are excluded — neither is an allergy, and both would otherwise match on the word "antibiotic"
   * alone.
   */
  private textNamesSensitivity(text: string, term: string): boolean {
    const haystack = (text || '').toLowerCase();
    if (!haystack.includes(term)) {
      return false;
    }
    return !this.isPremedicationLabel(haystack) && !haystack.includes('taking antibiotic');
  }

  private isCriticalSensitivityItemName(itemName: string): boolean {
    return this.CRITICAL_SENSITIVITY_TERMS.some(sensitivity => sensitivity.itemName === itemName);
  }

  /**
   * Stamps the canonical name on a matched sensitivity item, unless `claimName` is false — used
   * when this item's sensitivity term was already claimed by an earlier one, so the literal
   * canonical Field ID (e.g. "Pregnant") is never duplicated onto a second item. Antibiotics
   * additionally owns the free-text follow-up reporting WHICH antibiotic; that linking runs
   * regardless of `claimName`, since a duplicate antibiotics item can still have its own genuine
   * "Please indicate" follow-up that needs naming/parenting even though the item itself doesn't
   * get to keep the "AllergicToAntibiotics" name. Latex and epinephrine have no such follow-up
   * field downstream, so nothing else is linked for them.
   */
  private assignCriticalSensitivity(
    items: FormItemConfigurationModel[],
    index: number,
    sensitivity: { term: string; itemName: string },
    checkboxControlIds: string[],
    claimName: boolean
  ): void {
    if (claimName) {
      items[index].DFormItemName = sensitivity.itemName;
    }

    if (sensitivity.term === 'antibiotic') {
      this.nameAntibioticsFollowUp(items, index, checkboxControlIds);
    }
  }

  /**
   * Recognizes the free-text follow-up belonging to an allergy item ("Please indicate",
   * "Please describe reactions", ...). Broader than isExplanationLabel because the backend
   * prompt also allows "reaction"/"describe" wording here — see EnforceAntibioticsNoteBuilder
   * in TDOFormManager.cs, which note-enables the same set.
   */
  private isAllergyFollowUpLabel(label: string): boolean {
    const text = (label || '').trim().toLowerCase();
    return text.includes('indicate') ||
      text.includes('explain') ||
      text.includes('reaction') ||
      text.includes('describe');
  }

  /**
   * True when an item already carries one of the premedication canonical names. Those names
   * themselves contain "antibiotics", so without this guard the sensitivity pass would rename the
   * premedication answer/response it just assigned. matchesSensitivityTerm cannot catch it: the
   * camelCase name has no spaces, so the "required to take antibiotic" phrase never matches.
   */
  private isPremedicationItemName(itemName: string): boolean {
    return itemName === this.MEDICAL_HISTORY_ITEM_NAMES.premedicationLabel ||
      itemName === this.MEDICAL_HISTORY_ITEM_NAMES.premedicationAnswer ||
      itemName === this.MEDICAL_HISTORY_ITEM_NAMES.premedicationResponse;
  }

  /**
   * Applies the canonical item names medical history keys off downstream.
   *
   * Each critical sensitivity (see CRITICAL_SENSITIVITY_TERMS) is matched on label OR item name
   * and named "AllergicToLatex", "AllergicToAntibiotics", "AllergicToEpinephrine", or "Pregnant".
   * Both shapes the generator produces are recognized — a checklist checkbox and a Yes/No radio —
   * and for antibiotics the free-text follow-up reporting WHICH antibiotic is named
   * "AntibioticsPatientIndicate" and parented to it (overriding the generic preceding-question
   * link). The "required to take antibiotics prior to treatment" question label is named
   * "requiredToTakeAntibioticsLbl", its answer "requiredToTakeAntibioticsAns", and the following
   * "Please explain" response "requiredToTakeAntibioticsRes". Forms without these questions never
   * match and are unchanged.
   *
   * Runs in two passes so a dedicated item always wins the name: pass 1 matches an item's own
   * label/name, pass 2 lets a generically-labelled radio ("Yes/No") inherit the sensitivity term
   * from its question Paragraph. Without that ordering, a form carrying both a general "adverse
   * reactions to anesthetics/antibiotics/medications" question and a dedicated "Allergic to
   * Antibiotics" checkbox could hand the name to the general question — the dialog reads with
   * find(), so whichever comes first wins.
   *
   * A sensitivity term can only be claimed once across the whole draft application, not just this
   * paragraph — `claimedTerms` is shared by the caller across every added/replaced paragraph and
   * pre-seeded with sensitivities already claimed by untouched paragraphs elsewhere in the form.
   * If an item's term is already claimed, it never receives the literal canonical Field ID (and is
   * renamed off it if it already carries it verbatim) while its antibiotics follow-up, if any, is
   * still named/parented correctly; the collision is reported via `warnings` instead of silently
   * handing out the same Field ID twice.
   */
  private applyMedicalHistoryItemNames(
    paragraph: FormParagraphConfigurationModel,
    warnings: string[],
    claimedTerms: Set<string>
  ): void {
    const items = (paragraph?.cfgDFormItems || [])
      .filter(item => item && !item.xauditDeleted)
      .sort((a, b) =>
        (a.ParagraphRow || 0) - (b.ParagraphRow || 0) ||
        (a.ParagraphColumn || 0) - (b.ParagraphColumn || 0));

    // CONTROL_CHECK_BOX_LIST is included because the AI catalog aliases it as "checkbox" too
    // (see FormsConstants.cfgFormItemTypeList) — the AI is free to emit either shape for a
    // "checkbox" prompt, and both must be recognized as a critical-sensitivity checkbox.
    const checkboxControlIds = [
      this.globals.CONTROL_CHECK_BOX,
      this.globals.CONTROL_SMALL_CHECK_BOX,
      this.globals.CONTROL_CHECK_BOX_LIST
    ];
    const labelControlIds = [this.globals.CONTROL_LABEL, this.globals.CONTROL_LABEL_45];
    const answerControlIds = [this.globals.CONTROL_RADIO_LIST, this.globals.CONTROL_TEXT_BOX];

    // Question label each answer control sits under — the same "current question" notion
    // linkClinicalQuestionHierarchy uses. Only the fallback pass consults it.
    const questionLabelByIndex = new Map<number, string>();
    let currentQuestion = '';

    // Pass 1 — premedication, then sensitivities matched on the item's own label/name.
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (labelControlIds.includes(item.enumFormControlID)) {
        // A follow-up "Please explain" label must not hijack the parent question.
        if (!this.isExplanationLabel(item.Label)) {
          currentQuestion = item.Label || '';
        }

        // Premedication mentions antibiotics but is a medication requirement, not an allergy.
        if (this.isPremedicationLabel(item.Label)) {
          item.DFormItemName = this.MEDICAL_HISTORY_ITEM_NAMES.premedicationLabel;
          this.namePremedicationAnswerAndResponse(items, i, labelControlIds, answerControlIds);
        }
        continue;
      }

      const isCheckbox = checkboxControlIds.includes(item.enumFormControlID);
      const isRadio = item.enumFormControlID === this.globals.CONTROL_RADIO_LIST;
      if (!isCheckbox && !isRadio) {
        continue;
      }

      questionLabelByIndex.set(i, currentQuestion);

      if (this.isPremedicationItemName(item.DFormItemName)) {
        continue;
      }

      const matched = this.CRITICAL_SENSITIVITY_TERMS.find(sensitivity =>
        this.matchesSensitivityTerm(item, sensitivity.term));
      if (matched) {
        const alreadyClaimed = claimedTerms.has(matched.term);
        this.assignCriticalSensitivity(items, i, matched, checkboxControlIds, !alreadyClaimed);

        if (alreadyClaimed) {
          // Never let a second item keep the literal canonical name too — e.g. two "pregnant"
          // items both already named "Pregnant" would otherwise leave two items answering to the
          // same Field ID, ambiguous to the find()-based read side.
          if (item.DFormItemName === matched.itemName) {
            item.DFormItemName = `${matched.itemName}Duplicate`;
          }
          warnings.push(
            `Critical Sensitivity: "${item.Label || item.DFormItemName}" in "${paragraph.ParagraphName}" ` +
            `also names the "${matched.term}" sensitivity, but Field ID "${matched.itemName}" was already ` +
            `assigned elsewhere — left as "${item.DFormItemName}".`
          );
        } else {
          claimedTerms.add(matched.term);
        }
      }
    }

    // Pass 2 — the question Paragraph can carry the sensitivity term while its Yes/No radio is
    // labelled only "Yes/No". Restricted to radios: the checklist checkboxes share one generic
    // question ("...any of the following?"), which names no sensitivity term.
    questionLabelByIndex.forEach((questionLabel, index) => {
      const item = items[index];
      if (item.enumFormControlID !== this.globals.CONTROL_RADIO_LIST) {
        return;
      }
      if (this.isPremedicationItemName(item.DFormItemName) ||
        this.isCriticalSensitivityItemName(item.DFormItemName)) {
        return;
      }

      const matched = this.CRITICAL_SENSITIVITY_TERMS.find(sensitivity =>
        !claimedTerms.has(sensitivity.term) && this.textNamesSensitivity(questionLabel, sensitivity.term));
      if (matched) {
        this.assignCriticalSensitivity(items, index, matched, checkboxControlIds, true);
        claimedTerms.add(matched.term);
      }
    });
  }

  /**
   * Names and parents the free-text follow-up that reports WHICH antibiotic the patient reacts
   * to — the value the Medical History dialog shows next to "Antibiotics:". Scans forward from
   * the antibiotics item, stopping at the next allergy control so a distant text box in another
   * question is not grabbed, and prefers a "Please indicate"-style box over the first one seen
   * (a composite follow-up leaves its own Label empty, so the first box is the fallback).
   */
  private nameAntibioticsFollowUp(
    items: FormItemConfigurationModel[],
    antibioticsIndex: number,
    checkboxControlIds: string[]
  ): void {
    const antibioticsItem = items[antibioticsIndex];
    let fallback: FormItemConfigurationModel | null = null;

    for (let j = antibioticsIndex + 1; j < items.length; j++) {
      const candidate = items[j];

      // Another allergy answer starts a new question — stop before crossing into it.
      if (checkboxControlIds.includes(candidate.enumFormControlID) ||
        candidate.enumFormControlID === this.globals.CONTROL_RADIO_LIST) {
        break;
      }

      if (candidate.enumFormControlID !== this.globals.CONTROL_TEXT_BOX ||
        this.isPremedicationItemName(candidate.DFormItemName)) {
        continue;
      }

      if (this.isAllergyFollowUpLabel(candidate.Label)) {
        this.linkAntibioticsFollowUp(antibioticsItem, candidate);
        return;
      }

      fallback = fallback || candidate;
    }

    if (fallback) {
      this.linkAntibioticsFollowUp(antibioticsItem, fallback);
    }
  }

  private linkAntibioticsFollowUp(
    antibioticsItem: FormItemConfigurationModel,
    followUp: FormItemConfigurationModel
  ): void {
    followUp.ParentDFormItemId = antibioticsItem.DFormItemId;
    followUp.DFormItemName = this.MEDICAL_HISTORY_ITEM_NAMES.antibioticsPatientIndicate;
    antibioticsItem.HasChildren = true;
  }

  /**
   * Names the premedication question's answer control and the "Please explain" response that
   * follows it, stopping at the next real question label so a later question is not grabbed.
   */
  private namePremedicationAnswerAndResponse(
    items: FormItemConfigurationModel[],
    labelIndex: number,
    labelControlIds: string[],
    answerControlIds: string[]
  ): void {
    let answerNamed = false;

    for (let j = labelIndex + 1; j < items.length; j++) {
      const candidate = items[j];

      if (labelControlIds.includes(candidate.enumFormControlID) && !this.isExplanationLabel(candidate.Label)) {
        break;
      }

      if (!answerNamed && answerControlIds.includes(candidate.enumFormControlID)) {
        candidate.DFormItemName = this.MEDICAL_HISTORY_ITEM_NAMES.premedicationAnswer;
        answerNamed = true;
        continue;
      }

      // After the answer, the next text box is the "Please explain" response.
      if (answerNamed && candidate.enumFormControlID === this.globals.CONTROL_TEXT_BOX) {
        candidate.DFormItemName = this.MEDICAL_HISTORY_ITEM_NAMES.premedicationResponse;
        break;
      }
    }
  }

  /**
   * Canonical DFormItemName values the backend's MedicalHistoryController.GetMedicationHistory()
   * and the frontend's MedicationHistoryModel key off of. See FORMS_RES_MRX in
   * TDOPro.Domain/Constants/FormsConstants.cs (backend) and medication-history.model.ts (frontend).
   */
  private readonly MEDICATION_HISTORY_ITEM_NAMES = {
    medication: 'resMRXMedication',
    quantity: 'resMRXQuantity',
    frequency: 'resMRXFrequency',
    startDate: 'resMRXStartDate',
    reason: 'resMRXReason',
    lastTaken: 'resMRXLastTaken',
    stoppedTakingIt: 'resMRXStoppedTakingIt'
  } as const;

  /**
   * Applies the canonical item names medication history keys off downstream. Only ever touches a
   * repeating-paragraph table (isRepeting): row 0's Paragraph/Label header items are left alone
   * (excluded by control type, since headers and inputs share identical label text in the AI's
   * output), and row 1's input items are renamed to the fixed resMRX* names by matching each
   * item's own label text. Renaming only proceeds if the "medication" column is present AND at
   * least one other of the 7 columns also matched, so an unrelated repeating table (e.g.
   * "Emergency Contacts") that happens to share a generic word like "Reason" is left untouched.
   */
  private applyMedicationHistoryItemNames(paragraph: FormParagraphConfigurationModel): void {
    if (!paragraph || paragraph.isRepeting !== true) {
      return;
    }

    const labelControlIds = [this.globals.CONTROL_LABEL, this.globals.CONTROL_LABEL_45];
    const items = (paragraph.cfgDFormItems || [])
      .filter(item => item && !item.xauditDeleted && !labelControlIds.includes(item.enumFormControlID))
      .sort((a, b) =>
        (a.ParagraphRow || 0) - (b.ParagraphRow || 0) ||
        (a.ParagraphColumn || 0) - (b.ParagraphColumn || 0));

    const candidates = items
      .map(item => ({ item, name: this.matchMedicationHistoryColumn(item.Label) }))
      .filter((c): c is { item: FormItemConfigurationModel; name: string } => !!c.name);

    const matchedNames = new Set(candidates.map(c => c.name));
    if (!matchedNames.has(this.MEDICATION_HISTORY_ITEM_NAMES.medication) || matchedNames.size < 2) {
      return;
    }

    const assigned = new Set<string>();
    for (const { item, name } of candidates) {
      if (assigned.has(name)) continue;
      item.DFormItemName = name;
      assigned.add(name);
    }
  }

  /**
   * The "Repeat Paragraph" action's fixed DActionId. Referenced as a literal (not resolved from a
   * fetched action list) because the backend's AI prompt already teaches the model to emit this
   * exact GUID directly (see FormAiExamples.cs's afterUpdateActionId GUID table and its repeating-
   * paragraph examples) — it is a stable, environment-wide action, not per-form/dynamic data. See
   * also FormActionNameEnum.REPEAT_PARAGRAPH ('Repeat Paragraph') for the human-readable name.
   */
  private readonly REPEAT_PARAGRAPH_ACTION_ID = 'AC5E22EB-F135-4DD5-9F70-CCE6D5B8BA58';

  /**
   * Forces every Action Button item in a repeating paragraph to use the "Repeat Paragraph"
   * after-update action, regardless of what the AI returned (or omitted). Without this, the
   * generated "Add" button looks correct in the builder but silently does nothing when clicked.
   * No-op for non-repeating paragraphs.
   */
  private enforceRepeatParagraphAction(paragraph: FormParagraphConfigurationModel): void {
    if (!paragraph || paragraph.isRepeting !== true) {
      return;
    }

    (paragraph.cfgDFormItems || [])
      .filter(item => item && !item.xauditDeleted && item.enumFormControlID === this.globals.CONTROL_ACTION_BUTTON)
      .forEach(item => { item.AfterUpdate_ItemActionId = this.REPEAT_PARAGRAPH_ACTION_ID; });
  }

  private matchMedicationHistoryColumn(label: string): string | null {
    const text = (label || '').trim().toLowerCase();
    if (!text) return null;

    const names = this.MEDICATION_HISTORY_ITEM_NAMES;
    if (text.includes('stopped') || text.includes('discontinu')) return names.stoppedTakingIt;
    if (text.includes('last taken') || text.includes('last used') || text.includes('last dose')) return names.lastTaken;
    if (text.includes('start') || text.includes('began') || text.includes('date started')) return names.startDate;
    if (text.includes('reason') || text.includes('indication')) return names.reason;
    if (text.includes('frequency') || text.includes('how often')) return names.frequency;
    if (text.includes('quantity') || text.includes('dose') || text.includes('dosage') || text.includes('amount')) return names.quantity;
    if (text.includes('medication') || text.includes('drug name') || text.includes('med name')) return names.medication;
    return null;
  }

  private getControlNameById(controlTypeId: string): string {
    return FormsConstants.cfgFormItemTypeList.find(item => item.id === controlTypeId)?.name || 'Field';
  }

  private applyValidValueToItem(
    item: FormItemConfigurationModel,
    validValueName: string | undefined,
    validValues: any[]
  ): void {
    const cleanName = (validValueName || '').trim().toLowerCase();
    if (!cleanName) {
      return;
    }

    const validValue = (validValues || []).find((value: any) =>
      (value.ValidValueName || '').trim().toLowerCase() === cleanName
    );

    if (validValue) {
      item.ValidValueId = validValue.ValidValueId;
      item.ValidValue = JSON.parse(JSON.stringify(validValue));
    }
  }

  private findTemplateForAiItem(
    draftItem: AiGeneratedFormItem,
    templateFields: AiTemplateField[]
  ): AiTemplateField | null {
    const templates = templateFields || [];

    const byLabel = templates.find(template =>
      template.label?.trim().toLowerCase() === (draftItem.label || '').trim().toLowerCase()
    );
    if (byLabel) {
      return byLabel;
    }

    return templates.find(template =>
      template.controlType === draftItem.controlTypeId &&
      template.name?.trim().toLowerCase() === (draftItem.name || '').trim().toLowerCase()
    ) || null;
  }
}
